import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography } from '../ui';
import { BlockIcon, DeleteIcon } from '../icons';

interface WorkspaceDangerZoneProps {
  workspaceName: string;
  deactivated: boolean;
  togglingStatus: boolean;
  onToggleDeactivated: () => void;
  confirmDeleteOpen: boolean;
  setConfirmDeleteOpen: (open: boolean) => void;
  deleting: boolean;
  onConfirmDelete: () => void;
}

/**
 * WorkspaceDangerZone
 *
 * The destructive controls at the bottom of the workspace General tab.
 * Order follows the escalation: deactivate first (reversible) then delete
 * (irreversible, gated on being deactivated so a misclick can't wipe a
 * live tenant). State + the actual API calls live in the page; this is a
 * presentational shell over the two callbacks.
 */
export default function WorkspaceDangerZone({
  workspaceName,
  deactivated,
  togglingStatus,
  onToggleDeactivated,
  confirmDeleteOpen,
  setConfirmDeleteOpen,
  deleting,
  onConfirmDelete,
}: WorkspaceDangerZoneProps) {
  return (
    <>
      <Box
        sx={{
          mt: 4,
          p: 3,
          border: '1px solid',
          borderColor: '#f5c6cb',
          borderRadius: 2,
          bgcolor: '#fdf5f5',
        }}
      >
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            color: 'error.softText',
            fontWeight: 800,
            letterSpacing: '0.08em',
            mb: 0.5,
          }}
        >
          Danger zone
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Deactivate the workspace first to remove access for its members —
          once it's deactivated you can delete it permanently below.
        </Typography>

        {/* Deactivate / reactivate row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            py: 1.5,
            borderTop: '1px solid #f1d6d8',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700}>
              {deactivated ? 'Workspace is deactivated' : 'Deactivate workspace'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {deactivated
                ? 'Members render as inactive across the admin. Reactivate to restore access.'
                : 'Members lose access and render as inactive across the admin until reactivated.'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color={deactivated ? 'primary' : 'warning'}
            startIcon={<BlockIcon />}
            onClick={onToggleDeactivated}
            disabled={togglingStatus}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {deactivated ? 'Reactivate' : 'Deactivate'}
          </Button>
        </Box>

        {/* Delete row — gated on deactivation. The tooltip on the
            wrapper span fires even when the button is disabled. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            py: 1.5,
            borderTop: '1px solid #f1d6d8',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'error.softText' }}>
              Delete workspace
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {deactivated
                ? 'Permanent. Removes the workspace and its branding configuration. This cannot be undone.'
                : 'Deactivate the workspace first to enable deletion.'}
            </Typography>
          </Box>
          <Tooltip title={deactivated ? '' : 'Deactivate this workspace before deleting it.'}>
            <span>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={!deactivated || deleting}
                onClick={() => setConfirmDeleteOpen(true)}
                sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                Delete workspace
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Delete confirmation modal — second gate on top of the
          deactivation requirement above. */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => !deleting && setConfirmDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete workspace?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently delete <strong>{workspaceName || 'this workspace'}</strong>{' '}
            and its branding. Members and assigned content will be detached.
            This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} color="inherit" disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={onConfirmDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : 'Delete workspace'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
